import Phaser from 'phaser';
import { getGameContext } from '../utils/context';
import { SFX } from '../audio/AudioKeys';

export class Zombie extends Phaser.Physics.Arcade.Sprite {
    private currentDirection: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, 0);
    private speed: number = 50; // Slower than player
    private changeDirectionTimer: Phaser.Time.TimerEvent;

    constructor(scene: Phaser.Scene, x: number, y: number, texture: string, frame?: string | number) {
        super(scene, x, y, texture, frame);

        scene.add.existing(this);
        scene.physics.add.existing(this);

        this.setCollideWorldBounds(true);
        this.setRandomDirection();

        this.changeDirectionTimer = scene.time.addEvent({
            delay: Phaser.Math.Between(2000, 5000), // Change direction every 2-5 seconds
            callback: this.setRandomDirection,
            callbackScope: this,
            loop: true
        });
    }

    /**
     * Handle being hit by a projectile.
     * - Safely destroy the projectile
     * - Play per-hit SFX
     * - Delegate respawn/disable to provided callback if any
     */
    public handleProjectileHit(
        bullet: Phaser.GameObjects.GameObject | null | undefined,
        opts?: { onRespawn?: (z: Phaser.GameObjects.GameObject) => void }
    ): void {
        // Destroy the bullet safely
        if (bullet && !(bullet as any).destroyed) {
            try {
                (bullet as any).destroy?.();
            } catch {}
        }

        // Play "hit" SFX from the Zombie, not the Spawner
        this.playHitSfx();

        // Optionally delegate respawn/disable logic back to scene/spawner
        try {
            opts?.onRespawn?.(this);
        } catch {
            // ignore
        }
    }

    /**
     * Play a hit SFX when the zombie is struck by a projectile or attack.
     * Can be safely called by collision logic elsewhere.
     */
    public playHitSfx(): void {
        try {
            const { sound } = getGameContext(this.scene);
            const canPlay = !!this.scene?.cache?.audio?.has?.(SFX.EnemyHit);
            if (canPlay) {
                sound?.playSfx(SFX.EnemyHit, { volume: 0.9 });
            }
        } catch {
            // ignore if context not ready
        }
    }

    private setRandomDirection(): void {
        const directions = [
            new Phaser.Math.Vector2(0, -1), // Up
            new Phaser.Math.Vector2(0, 1),  // Down
            new Phaser.Math.Vector2(-1, 0), // Left
            new Phaser.Math.Vector2(1, 0),  // Right
            new Phaser.Math.Vector2(0, 0)   // Stop
        ];
        this.currentDirection = Phaser.Math.RND.pick(directions);
        this.setVelocity(this.currentDirection.x * this.speed, this.currentDirection.y * this.speed);
    }

    update(): void {
        // Zombies will change direction periodically or on collision (handled by physics)
    }

    destroy(): void {
        this.changeDirectionTimer.destroy();
        super.destroy();
    }
}
